import { useContext, useMemo, useState, useEffect } from 'react'
import { Link } from 'react-router'
import { ROUTES } from '@/config/routes'
import { Footer }  from '@/components/Footer'
import { InfoCard } from '@/components/InfoCard'
import { Button } from '@/components/Button'
import { Heading } from '@/components/Heading'
import { ApplyidLogo } from '@/components/ApplyidLogo'
import { Header } from '@/components/HeaderStart'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { DealerContext } from '@/context/DealerContext'
import { defaultMinPersonalLoanAmount } from '@/utils/constant'
import { formatNumberWithCommas } from '@/utils/helper'
import useFormContext from '@/context/useFormContext'

function PersonalPage() {
  const [isChecked, setIsChecked] = useState(false)
  const navigate = usePersistedNavigation()
  const dealerContext = useContext(DealerContext)
  const isDealer = dealerContext?.isDealer ?? false
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked)
  }

  const handleButtonClick = () => {
    if (isChecked) {
      navigate(ROUTES.PERSONAL.YOUR_DETAILS.path)
    } else {
      // This in theory should never happen, because we disable the button when the checkbox is not checked
      window.alert('Please agree to the terms before proceeding')
    }
  }

  const minAmount = dealerContext?.dealerInformation.minimumLoanAmount || defaultMinPersonalLoanAmount
  const formattedMinAmount = useMemo(() => 
   formatNumberWithCommas(minAmount) || 0
  , [dealerContext?.dealerInformation.minimumLoanAmount]) 

  useEffect(() => {
    document.title = 'UDC Finance | Individual Application'
  }, [])

  const { dispatch } = useFormContext()
  useEffect(() => {
    // We need to attach required dealer details to the form context
    if (dealerContext?.dealerInformation.dealerId || dealerContext?.dealerInformation.dealerUniqueIdentifier) {
      dispatch({
        type: 'UPDATE_DEALER_DETAILS',
        payload: {
          dealerId: dealerContext?.dealerInformation.dealerId ?? null,
          dealerUniqueIdentifier: dealerContext?.dealerInformation.dealerUniqueIdentifier ?? null
        }
      })
    }
  }, [dealerContext?.dealerInformation.dealerId, dealerContext?.dealerInformation.dealerUniqueIdentifier, dispatch])

  return (
    <>
    <Header />
    <section className='px-4 md:px-6 lg:px-10 pb-16 lg:pb-20 grow'>
      <Heading>Before you apply, you will need:</Heading>
      <div className='max-w-6xl mx-auto justify-center flex flex-col md:flex-row max-md:items-center gap-2 md:gap-8'>
        <InfoCard icon={dealerContext?.dealerThemeIcon?.nzLicense || '/icons/info-card/drivers-licence.svg'} iconDescription='Drivers Licence' align='top'>
          <p>Your current New Zealand full or restricted Driver Licence</p>
        </InfoCard>
        <InfoCard icon={dealerContext?.dealerThemeIcon?.employmentDetails || '/icons/info-card/employment-details.svg'} iconDescription='Employment details' align='top'>
          <p>Your employment details</p>
        </InfoCard>
        <InfoCard icon={dealerContext?.dealerThemeIcon?.financialStanding || '/icons/info-card/income-details.svg'} iconDescription='Income details' align='top'>
          <p>Details of your income, expenses, assets & liabilities</p>
        </InfoCard>
      </div>
      <Heading as='h2'>In order to proceed with this application:</Heading>
      <div className='max-w-6xl mx-auto justify-center flex flex-col md:flex-row max-md:items-center gap-2 md:gap-8 '>
        <InfoCard>
          <p>You must be a New Zealand citizen or have a New Zealand resident visa and you must be at least 18 years old</p>
        </InfoCard>
        <InfoCard>
          <p>The loan amount you are applying for must be at least ${formattedMinAmount}</p>
        </InfoCard>
      </div>
    
      <div className='flex gap-3 max-w-4xl mx-auto lg:px-10 pt-20 pb-12'>
        <div className='flex h-6 shrink-0 items-center'>
          <div className='group grid size-8 grid-cols-1'>
            <input
              id='agreeStart'
              name='agreeStart'
              type='checkbox'
              aria-describedby='agreeStartDescription'
              className='size-8 cursor-pointer col-start-1 row-start-1 appearance-none rounded border border-[var(--theme-text)] bg-white checked:border-[var(--checkbox-active-bg)] checked:bg-[var(--checkbox-active-bg)] indeterminate:border-[var(--checkbox-active-bg)] indeterminate:bg-[var(--checkbox-active-bg)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--checkbox-active-bg)] disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto'
              onChange={handleCheckboxChange}
            />
            <svg
              fill='none'
              viewBox='0 0 14 14'
              className='pointer-events-none col-start-1 row-start-1  self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25'
            >
              <path
                d='M3 8L6 11L11 3.5'
                strokeWidth={1}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='opacity-0 group-has-[:checked]:opacity-100'
              />
              <path
                d='M3 7H11'
                strokeWidth={1}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='opacity-0 group-has-[:indeterminate]:opacity-100'
              />
            </svg>
          </div>
        </div>
        <div className='text-sm/6'>
          <label htmlFor='agreeStart' className='text-lg cursor-pointer'>
            By ticking this box, you confirm that:
          </label>
          <ul id='agreeStartDescription' className='text-lg/6 list-disc list-outside pl-8 _simple-rte'>
            <li>You meet each of the requirements set out above</li>
            <li>You have read <strong><a href='https://www.udc.co.nz/website-security-and-privacy-statement' target='_blank' rel='noopener noreferrer'>UDC's Privacy Statement</a></strong> and you understand that it applies to your loan application</li>
            <li>You have read <strong><a href='https://www.udc.co.nz/financial-advice-disclosure-statement' target='_blank' rel='noopener noreferrer'>UDC's Financial Advice Statement</a></strong> and you understand that any recommendations that we may give you in relation to your loan application are not "regulated financial advice" under New Zealand law</li>
            <li>You understand that this application is subject to responsible lending inquiries and that UDC's loan eligibility criteria, fees, terms and conditions apply. </li>
          </ul>
        </div>
      </div>

      <div className='flex justify-center py-4'>
        <Button 
          icon='default' 
          disabled={!isChecked}
          onClick={handleButtonClick}
        >Start application</Button>
      </div>

      <Heading as='h2'>To make things easier for you we now provide</Heading>
        <div className='mx-auto w-full max-w-[365px] text-lg text-center -mt-4'>
          <div className='bg-[var(--info-card-bg)] text-black py-8 px-6 rounded-[4px] text-left space-y-8 mb-4'>
            <span className='block text-sm'>
              ID verification made easy with:
            </span>
            <div className='flex justify-center py-1'>
              <ApplyidLogo />
            </div>
          </div>
          <p>We use APLYiD to verify your identity via your mobile phone</p><Link className='underline text-[var(--theme-link-text)]' to='https://www.udc.co.nz/apply_moreinfo' target='_blank'>More information about APLYiD</Link>
        </div>
    </section>
    {!isDealer && <Footer />}
    </>
  )
}

export default PersonalPage
